import { from } from "fromfrom";
import produce from "immer";
import _ from "lodash";
import { missingIds, reduceById, uniquePropsById } from "redux-saga-tools";
import { createSelector } from "reselect";
import { assignsSelector } from "../../store/assigns.store";
import { employeesSelector } from "../../store/employees.store";
import { groupsSelector } from "../../store/groups.store";
import { RootState } from "../../store/index.store";
import { jobsSelector } from "../../store/jobs.store";
import { masterAssignsSelector } from "../../store/master-assigns.store";
import { Assign, Employee, Job, KeyValue, NenkiInfo } from "../../types/types";
import {
  calcSagyoKosu,
  findById,
  nameof,
  roundByPrecision,
  sumNumber,
} from "../../util/util";
import { AssignChanges } from "./assign-page.store";

/**
 * アサイン画面のRoot Stateを返す
 * @param state
 */
const root = (state: RootState) => state.page.assign;
const getRootState = root;

/**
 * 年期を返す
 * @param state
 */
const getNenki = (state: RootState) => root(state).nenki;

const getNenkiInfo = createSelector<RootState, string, NenkiInfo>(
  getNenki,
  (nenki) => {
    const currentHalf = nenki.substr(2, 1) as "A" | "B";

    return {
      currentNenki: nenki,
      currentHalf,
      months:
        currentHalf === "A"
          ? [
              { month: 4, columnName: "month_1st" },
              { month: 5, columnName: "month_2nd" },
              { month: 6, columnName: "month_3rd" },
              { month: 7, columnName: "month_4th" },
              { month: 8, columnName: "month_5th" },
              { month: 9, columnName: "month_6th" },
            ]
          : [
              { month: 10, columnName: "month_1st" },
              { month: 11, columnName: "month_2nd" },
              { month: 12, columnName: "month_3rd" },
              { month: 1, columnName: "month_4th" },
              { month: 2, columnName: "month_5th" },
              { month: 3, columnName: "month_6th" },
            ],
    };
  }
);

/**
 * 現在選択されているグルーピング条件を返す
 * @param state
 */
const getSelectedGroupingCondition = (state: RootState) =>
  root(state).selectedGroupingCondition;

/**
 * デフォルトフィルターを返す
 * @param state
 */
const getDefaultFilters = (state: RootState) => root(state).defaultFilters;

/**
 * カスタムフィルターを返す
 * @param state
 */
const getCustomFilters = (state: RootState) => root(state).customFilters;

/**
 * 選択されているアサインのIDを返す
 * @param state
 */
const getSelectedAssignId = (state: RootState) => root(state).selectedAssignId;

/**
 * デフォルトグルーピング展開状態を返す
 * @param state
 */
const getDefaultGroupingExpansionState = (state: RootState) =>
  root(state).defaultGroupingExpansionState;

/**
 * 選択されているアサインを返す
 */
const getSelectedAssign = createSelector(
  assignsSelector,
  getSelectedAssignId,
  (assigns, selectedAssignsId) => findById(assigns, selectedAssignsId)
);

/**
 * アサインの順番を返す
 * @param state
 */
const getAssignOrder = (state: RootState) => root(state).assignOrder;

/**
 * 選択されている従業員を返す
 */
const getSelectedEmployee = createSelector(
  employeesSelector,
  getSelectedAssign,
  (employees, assign) => {
    if (!assign) {
      return undefined;
    }
    return employees.find((v) => v.username === assign.inChargeUsername);
  }
);

/**
 * 選択されているジョブを返す
 */
const getSelectedJob = createSelector(
  jobsSelector,
  getSelectedAssign,
  (jobs, assign) => {
    if (!assign) {
      return undefined;
    }
    return findById(jobs, assign.jobCode);
  }
);

/**
 * 作業工数を返す
 */
const getSagyoKosu = createSelector(getSelectedJob, (job) => {
  if (!job) {
    return;
  }
  return calcSagyoKosu(job.hikiai);
});

/**
 * 選択されているジョブのアサインサマリーを返す
 * @param state
 */
const getSelectedJobAssignSummary = (state: RootState) => {
  const job = getSelectedJob(state);
  const assigns = assignsSelector(state);
  const employee = employeesSelector(state);

  if (!job) {
    return;
  }
  const jobAssigns = assigns.filter((v) => v.jobCode === job.id);
  const byEmployee = jobAssigns.map((a) => {
    const e = employee.find((v) => v.username === a.inChargeUsername);
    return {
      name: e ? e.fullName : "",
      total:
        (a.month_1st || 0) +
        (a.month_2nd || 0) +
        (a.month_3rd || 0) +
        (a.month_4th || 0) +
        (a.month_5th || 0) +
        (a.month_6th || 0),
    };
  });
  return {
    total: _.sumBy(byEmployee, nameof("total")),
    byEmployee,
  };
};

/**
 * 変更されたアサインを返す
 */
const getAssignChanges = createSelector(
  masterAssignsSelector,
  assignsSelector,
  (master, assigns) => {
    const masterById = reduceById(master);
    const assignsById = reduceById(assigns);
    const ids = uniquePropsById(master.concat(assigns), "id");
    const addedIds = missingIds(masterById, ids);
    const removedIds = missingIds(assignsById, ids);

    const itemsNewAdded: Assign[] = _.cloneDeep(assigns)
      .filter((v) => addedIds.includes(v.id.toString()))
      .map((v) => {
        v.status = "added";
        return v;
      });
    const itemsDummyAdded: Assign[] = [];
    master
      .filter(
        (v: Assign) =>
          !addedIds.includes(v.id.toString()) &&
          !removedIds.includes(v.id.toString())
      )
      .forEach((v: Assign) => {
        const find = findById(assigns, v.id);
        if (find && find.status === "dummy" && !_.isEqual(v, find)) {
          const deep = _.cloneDeep(find);
          deep.status = "added";
          itemsDummyAdded.push(deep);
        }
      });
    const itemsAdded: Assign[] = itemsNewAdded.concat(itemsDummyAdded);

    const itemsRemoved: Assign[] = _.cloneDeep(master)
      .filter((v) => removedIds.includes(v.id.toString()))
      .map((v) => {
        v.status = "removed";
        return v;
      });

    const itemsEdited: Assign[] = [];
    master
      .filter(
        (v: Assign) =>
          !addedIds.includes(v.id.toString()) &&
          !removedIds.includes(v.id.toString())
      )
      .forEach((v: Assign) => {
        const find = findById(assigns, v.id);
        if (find && find.status !== "dummy" && !_.isEqual(v, find)) {
          const deep = _.cloneDeep(find);
          deep.status = "edited";
          itemsEdited.push(deep);
        }
      });

    const changes: AssignChanges = {
      itemsAdded,
      itemsEdited,
      itemsRemoved,
    };

    return changes;
  }
);

/**
 * テキスト検索候補を返す
 */
const getTextSearchSuggetions = createSelector(
  assignsSelector,
  jobsSelector,
  employeesSelector,
  groupsSelector,
  (assigns, jobs, employees, groups) => {
    // 客先
    const clientNames = _.uniq(assigns.map((v) => v.clientName));
    const client: KeyValue[] = clientNames.map((v) => ({
      key: v,
      value: v + textFilterCategories.client,
    }));

    // ジョブ
    const job: KeyValue[] = jobs.map((v) => ({
      key: v.jobNo + v.jobName, // ジョブ名だけだとキーが被ってしまう可能性がある
      value: v.jobName + textFilterCategories.job,
    }));

    // 担当
    const emp: KeyValue[] = employees.map((v) => ({
      key: v.username,
      value: v.fullName + textFilterCategories.tanto,
    }));

    // グループ（ジョブ）
    const group: KeyValue[] = groups.map((v) => ({
      key: `groupjob_${v.id}`,
      value: v.groupName + textFilterCategories.group,
    }));

    // グループ（メンバー）
    const groupMember: KeyValue[] = groups.map((v) => ({
      key: `groupmember_${v.id}`,
      value: v.groupName + textFilterCategories.groupMember,
    }));

    const list = client.concat(job, emp, group, groupMember);
    return list.filter((v) => v.key !== undefined);
  }
);

/**
 * テキスト検索条件を返す
 * @param state
 */
const getTextSearchConditions = (state: RootState) =>
  root(state).textSearchConditions;

/**
 * テキスト検索条件適用後のアサインを返す
 */
const getTextFilteredAssigns = createSelector(
  assignsSelector,
  employeesSelector,
  getTextSearchConditions,
  (assigns, employees, conditions) => {
    return filterByCondition(assigns, employees, conditions);
  }
);

/**
 * 全てのフィルターを返す
 * @param state
 */
const getFilters = createSelector(
  getDefaultFilters,
  getCustomFilters,
  getTextFilteredAssigns,
  getAssignChanges,
  jobsSelector,
  employeesSelector,
  (defaultFilters, customFilters, assigns, changes, jobs, employees) => {
    const filters = defaultFilters.concat(customFilters);
    const changedCount =
      changes.itemsAdded.length +
      changes.itemsEdited.length +
      changes.itemsRemoved.length;

    return produce(filters, (draft) => {
      draft.forEach((v) => {
        switch (v.id) {
          case "all":
            v.itemCount = assigns.length;
            break;
          case "unfilled_job":
            v.itemCount = getUnfilledAssignsByJob(assigns, jobs).length;
            break;
          case "unfilled_employee":
            v.itemCount = getUnfilledAssignsByEmployee(assigns).length;
            break;
          case "unsaved_changes":
            v.itemCount = changedCount;
            break;
          default:
            v.itemCount = filterByCondition(
              assigns,
              employees,
              v.customFilterCondition
            ).length;
        }
      });
    });
  }
);

/**
 * 現在選択されているフィルタのIDを返す
 * @param state
 */
const getSelectedFilterId = (state: RootState) => root(state).selectedFilterId;

/**
 * 現在選択されているフィルタを返す
 */
const getSelectedFilter = createSelector(
  getSelectedFilterId,
  getFilters,
  (selectedFilterId, filters) => {
    const f = filters.find((v) => v.id === selectedFilterId);
    if (!f) {
      throw new Error("No filter selected.");
    }
    return f;
  }
);

/**
 * 指定されたIDのフィルタを返す
 * @param state
 * @param id
 */
const getFilterById = (state: RootState, id: string) => {
  const filters = getFilters(state);
  const filter = filters.find((v) => v.id === id);
  if (!filter) {
    throw new Error("Filter not found.");
  }
  return filter;
};

/**
 * フィルタリングされたアサインを返す
 */
const getFilteredAssigns = createSelector(
  getTextFilteredAssigns,
  getAssignChanges,
  getSelectedFilter,
  jobsSelector,
  employeesSelector,
  (assigns, changes, filter, jobs, employees) => {
    switch (filter.id) {
      case "all":
        return assigns;
      case "unfilled_job":
        return getUnfilledAssignsByJob(assigns, jobs);
      case "unfilled_employee":
        return getUnfilledAssignsByEmployee(assigns);
      case "unsaved_changes":
        return changes.itemsAdded
          .concat(changes.itemsEdited)
          .concat(changes.itemsRemoved);
      default:
        return filterByCondition(
          assigns,
          employees,
          filter.customFilterCondition
        );
    }
  }
);

/**
 * グリッドの状態を返す
 * @param state
 */
const getGridState = (state: RootState) => root(state).gridState;

const textFilterCategories = {
  client: " (客先)",
  job: " (Job)",
  tanto: " (担当)",
  group: " (グループ)",
  groupMember: " (メンバー)",
};

/**
 * 渡された条件でアサインをフィルタリングする
 * @param assigns
 * @param employees
 * @param conditions
 */
const filterByCondition = (
  assigns: Assign[],
  employees: Employee[],
  conditions?: KeyValue[]
) => {
  if (!conditions) {
    return assigns;
  }

  const clientCondition = conditions
    .filter((v) => v.value.endsWith(textFilterCategories.client))
    .map((v) => v.value.replace(textFilterCategories.client, ""));
  const jobCondition = conditions
    .filter((v) => v.value.endsWith(textFilterCategories.job))
    .map((v) => v.value.replace(textFilterCategories.job, ""));
  const inChargeCondition = conditions
    .filter((v) => v.value.endsWith(textFilterCategories.tanto))
    .map((v) => v.value.replace(textFilterCategories.tanto, ""));
  const groupCondition = conditions
    .filter((v) => v.value.endsWith(textFilterCategories.group))
    .map((v) => v.value.replace(textFilterCategories.group, ""));
  const groupMemberCondition = conditions
    .filter((v) => v.value.endsWith(textFilterCategories.groupMember))
    .map((v) => v.key.split("_")[1]);

  return conditions.length === 0
    ? assigns
    : assigns.filter((v) => {
        return (
          (clientCondition.length === 0
            ? true
            : clientCondition.includes(v.clientName)) &&
          (jobCondition.length === 0
            ? true
            : jobCondition.includes(v.jobName)) &&
          (inChargeCondition.length === 0
            ? true
            : inChargeCondition.includes(v.inChargeName)) &&
          (groupCondition.length === 0
            ? true
            : groupCondition.some((gc) => v.buName.indexOf(gc) >= 0) ||
              groupCondition.includes(v.groupName)) &&
          (groupMemberCondition.length === 0
            ? true
            : matchAssignsByGroupAndMember(v, employees, groupMemberCondition))
        );
      });
};

const matchAssignsByGroupAndMember = (
  assign: Assign,
  employees: Employee[],
  groups: string[]
): boolean => {
  const targetEmployeeCodes = employees
    .filter((v) => groups.includes(v.shozokuCode))
    .map((v) => v.kojinCode);
  return targetEmployeeCodes.includes(assign.inChargeKojinCode ?? "");
};

/**
 * 未充足ジョブのアサインを返す
 * @param assigns
 * @param jobs
 */
const getUnfilledAssignsByJob = (assigns: Assign[], jobs: Job[]) => {
  const jobKosuTotal = from(assigns)
    .groupBy("jobCode")
    .map((item) => ({
      jobCode: item.key,
      kosuTotal: roundByPrecision(
        item.items.reduce(
          (a, c) =>
            a +
            sumNumber(
              c.month_1st,
              c.month_2nd,
              c.month_3rd,
              c.month_4th,
              c.month_5th,
              c.month_6th
            ),
          0
        ),
        2
      ),
    }))
    .filter((item) => {
      const job = from(jobs).find((item1) => item1.id === item.jobCode);
      const hikiai = job ? job.hikiai : 0;
      return item.kosuTotal < calcSagyoKosu(hikiai);
    })
    .map((item) => item.jobCode)
    .toArray();

  return from(assigns)
    .filter((item) => jobKosuTotal.includes(item.jobCode))
    .toArray();
};

/**
 * 未充足従業員のアサインを返す
 * @param assigns
 */
const getUnfilledAssignsByEmployee = (assigns: Assign[]) => {
  const empKosuTotal = from(assigns)
    .groupBy("inChargeUsername")
    .map((item) => ({
      inChargeUsername: item.key,
      kosuTotal: roundByPrecision(
        item.items.reduce(
          (a, c) =>
            a +
            sumNumber(
              c.month_1st,
              c.month_2nd,
              c.month_3rd,
              c.month_4th,
              c.month_5th,
              c.month_6th
            ),
          0
        ),
        2
      ),
    }))
    .filter((item) => item.kosuTotal < 6)
    .map((item) => item.inChargeUsername)
    .toArray();

  return from(assigns)
    .filter((item) => empKosuTotal.includes(item.inChargeUsername))
    .toArray();
};

export const assignPageSelectors = {
  getRootState,
  getNenki,
  getNenkiInfo,
  getSelectedGroupingCondition,
  getDefaultFilters,
  getCustomFilters,
  getSelectedAssignId,
  getDefaultGroupingExpansionState,
  getSelectedAssign,
  getAssignOrder,
  getSelectedEmployee,
  getSelectedJob,
  getSagyoKosu,
  getSelectedJobAssignSummary,
  getAssignChanges,
  getTextSearchSuggetions,
  getTextSearchConditions,
  getTextFilteredAssigns,
  getFilters,
  getSelectedFilterId,
  getSelectedFilter,
  getFilterById,
  getFilteredAssigns,
  getGridState,
};
